"use client";

import { Icon } from "@iconify-icon/react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import type { ComponentProps, HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import { tv } from "tailwind-variants";

import { cn } from "@/services/styles/cn";

import { Button } from "../button/button.component";
import { getCarouselDotButtonClass } from "./carousel.styles";

// TODO - Add tests for Carousel;

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollTo: (index: number) => void;
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = use(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  ref,
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & CarouselProps & { ref?: React.RefObject<HTMLDivElement | null> }) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((eventApi: CarouselApi) => {
    if (!eventApi) {
      return;
    }

    /* eslint-disable @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- its used in multiple places outside of useEffect */
    setCanScrollPrev(eventApi.canScrollPrev());
    setCanScrollNext(eventApi.canScrollNext());
    /* eslint-enable */
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  const value = useMemo(
    () => ({
      carouselRef,
      api,
      scrollTo,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      orientation,
      opts,
      plugins,
      setApi,
    }),
    [
      carouselRef,
      api,
      scrollPrev,
      scrollNext,
      scrollTo,
      canScrollPrev,
      canScrollNext,
      orientation,
      opts,
      plugins,
      setApi,
    ],
  );

  return (
    <CarouselContext value={value}>
      <div
        ref={ref}
        className={tv({ base: "relative" })({ className })}
        // biome-ignore lint/a11y/useSemanticElements: we want it not to be <section>
        role="region"
        aria-roledescription="carousel"
        onKeyDownCapture={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CarouselContext>
  );
}
Carousel.displayName = "Carousel";

function CarouselContent({
  ref,
  className,
  classNameWrapper,
  ...props
}: HTMLAttributes<HTMLDivElement> & { classNameWrapper?: string } & { ref?: React.RefObject<HTMLDivElement | null> }) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className={tv({ base: ["overflow-hidden"] })({ className: classNameWrapper })}>
      <div
        ref={ref}
        className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)}
        {...props}
      />
    </div>
  );
}
CarouselContent.displayName = "CarouselContent";

function CarouselItem({
  ref,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { ref?: React.RefObject<HTMLDivElement | null> }) {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={tv({ base: ["min-w-0 shrink-0 grow-0 basis-full", orientation === "horizontal" ? "pl-4" : "pt-4"] })({
        className,
      })}
      {...props}
    />
  );
}
CarouselItem.displayName = "CarouselItem";

function CarouselPrevious({
  ref,
  className,
  children,
  variant = "outline",
  size = "icon",
  ...props
}: ComponentProps<typeof Button> & { ref?: React.RefObject<HTMLButtonElement | null> }) {
  const { orientation, scrollPrev, canScrollPrev, canScrollNext } = useCarousel();

  if (!canScrollPrev && !canScrollNext) {
    return null;
  }

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      disabled={!canScrollPrev}
      className={tv({
        base: [
          "absolute text-2xl",
          orientation === "horizontal"
            ? "-left-12 -translate-y-1/2 top-1/2"
            : "-top-12 -translate-x-1/2 left-1/2 rotate-90",
        ],
      })({ className })}
      onClick={scrollPrev}
      {...props}
    >
      {children ?? <Icon icon="mdi:arrow-left" />}
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}
CarouselPrevious.displayName = "CarouselPrevious";

function CarouselNext({
  ref,
  className,
  children,
  variant = "outline",
  size = "icon",
  ...props
}: ComponentProps<typeof Button> & { ref?: React.RefObject<HTMLButtonElement | null> }) {
  const { orientation, scrollNext, canScrollPrev, canScrollNext } = useCarousel();

  if (!canScrollPrev && !canScrollNext) {
    return null;
  }

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      disabled={!canScrollNext}
      className={tv({
        base: [
          "absolute text-2xl",
          orientation === "horizontal"
            ? "-right-12 -translate-y-1/2 top-1/2"
            : "-bottom-12 -translate-x-1/2 left-1/2 rotate-90",
        ],
      })({ className })}
      onClick={scrollNext}
      {...props}
    >
      {children ?? <Icon icon="mdi:arrow-right" />}
      <span className="sr-only">Next slide</span>
    </Button>
  );
}
CarouselNext.displayName = "CarouselNext";

function CarouselDotButton({
  ref,
  className,
  children,
  index,
  active,
  ...props
}: ComponentProps<typeof Button> & { index: number; active: boolean } & {
  ref?: React.RefObject<HTMLButtonElement | null>;
}) {
  const { scrollTo } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      className={getCarouselDotButtonClass({ className, active })}
      onClick={() => scrollTo(index)}
      {...props}
    >
      {children}
    </button>
  );
}
CarouselDotButton.displayName = "CarouselDotButton";

function CarouselDotNavigation({
  ref,
  className,
  items,
  ...props
}: HTMLAttributes<HTMLDivElement> & { items: (selectedIndex: number) => ReactNode } & {
  ref?: React.RefObject<HTMLDivElement | null>;
}) {
  const { api } = useCarousel();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    setSelectedIndex(api?.selectedScrollSnap() ?? 0);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <div ref={ref} className={tv({ base: "flex justify-center gap-3" })({ className })} {...props}>
      {items(selectedIndex)}
    </div>
  );
}
CarouselDotNavigation.displayName = "CarouselDotNavigation";

export {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDotButton,
  CarouselDotNavigation,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
};
